import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { Button, Spinner } from 'react-bootstrap';
import surveyActivationService from '../../services/surveyActivationService';
import { toast } from 'react-toastify';
import { AuthContext } from '../../contexts/AuthContext';
import { canActivateVisits } from '../../utils/activationPermissions';

/**
 * WindowActivationButton - A reusable button component that works with the activateOrDeactivateWindow function
 * to activate or deactivate survey windows for a participant.
 * 
 * @component
 */
const WindowActivationButton = ({
  participantId,
  windowIds,
  activate = true,
  customLabel,
  customStyles = {},
  variant = 'primary',
  size = 'sm',
  className = '',
  onSuccess = () => {},
  onError = () => {},
  disabled = false
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useContext(AuthContext);
  
  // Check if user has permission to activate/deactivate visits
  const hasPermission = canActivateVisits(user);
  
  // If user doesn't have permission, don't render the button
  if (!hasPermission) {
    return null;
  }
  
  // Determine the button text based on the activate flag and customLabel
  const getButtonText = () => {
    if (customLabel) return customLabel;
    return activate ? 'Activate Window' : 'Deactivate Window';
  };

  // Handle the button click event
  const handleClick = async () => {
    if (!participantId || !windowIds || windowIds.length === 0) {
      toast.error('Missing required parameters: participantId or windowIds');
      onError(new Error('Missing required parameters'));
      return;
    }

    setIsLoading(true);
    try {
      const result = await surveyActivationService.activateOrDeactivateWindow(
        participantId,
        windowIds,
        activate
      );
      
      // Show success message
      const actionText = activate ? 'activated' : 'deactivated';
      toast.success(`Window successfully ${actionText}`);
      
      // Call onSuccess callback with the result
      onSuccess(result);
    } catch (error) {
      // Show error message
      const actionText = activate ? 'activating' : 'deactivating';
      toast.error(`Error ${actionText} window: ${error.message || 'Unknown error'}`);
      
      // Call onError callback with the error
      onError(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      className={className}
      onClick={handleClick}
      disabled={disabled || isLoading}
      style={customStyles}
    >
      {isLoading ? (
        <>
          <Spinner
            as="span"
            animation="border"
            size="sm"
            role="status"
            aria-hidden="true"
            className="me-2"
          />
          <span>Processing...</span>
        </>
      ) : (
        getButtonText()
      )}
    </Button>
  );
};

WindowActivationButton.propTypes = {
  /** Participant ID for the window activation/deactivation */
  participantId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  
  /** Array of window IDs to activate/deactivate */
  windowIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  
  /** Flag to determine if activating (true) or deactivating (false) the window */
  activate: PropTypes.bool,
  
  /** Custom label text for the button */
  customLabel: PropTypes.string,
  
  /** Custom styles object for the button */
  customStyles: PropTypes.object,
  
  /** Button variant (bootstrap variant) */
  variant: PropTypes.string,
  
  /** Button size (bootstrap size) */
  size: PropTypes.string,
  
  /** Additional CSS class names */
  className: PropTypes.string,
  
  /** Callback function called on successful activation/deactivation */
  onSuccess: PropTypes.func,
  
  /** Callback function called on error */
  onError: PropTypes.func,
  
  /** Flag to disable the button */
  disabled: PropTypes.bool
};

export default WindowActivationButton;